import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { AdminPageHeader, NoteCard, RING, useAdminShell } from '../../components/admin/shell';

/**
 * NOW › TODAY — spec/today.md. Only the queues the person can act on, from the same
 * counts the rail badges use. A card with a zero count still renders; when every card is
 * filtered out, the note box renders alone.
 */
export default function TodayPage() {
  const navigate = useNavigate();
  const { can, isSuperadmin } = useUser();
  const { counts } = useAdminShell();
  const c = counts || {};
  const n = (v) => (v == null ? '—' : v.toLocaleString());
  const plural = (v, one, many) => (v === 1 ? one : many);

  const cards = [
    c.openUserReports != null && {
      label: 'USER REPORTS', count: c.openUserReports, cta: 'OPEN REPORTS', to: '/admin/user-reports',
      note: c.openUserReports ? 'People waiting on an answer. Both app stores expect one within a day.' : 'Nothing waiting.',
    },
    can('ingestion', 'view') && {
      label: 'INGESTION QUEUE', count: c.ingestionQueue, cta: 'OPEN QUEUE', to: '/admin/ingestion/queue',
      note: `Scraped events waiting on approval.${c.failingSources ? ` ${c.failingSources} ${plural(c.failingSources, 'source is', 'sources are')} failing.` : ''}${c.eventClaims ? ` ${c.eventClaims} host ${plural(c.eventClaims, 'claim', 'claims')} waiting.` : ''}`,
    },
    can('disputes', 'view') && {
      label: 'OPEN DISPUTES', count: c.openDisputes, cta: 'OPEN DISPUTES', to: '/admin/disputes',
      note: c.openDisputes ? 'Each one holds escrow until a human decides.' : 'Nothing waiting.',
    },
    can('withdrawals', 'view') && {
      label: 'WITHDRAWALS WAITING', count: c.pendingWithdrawals, cta: 'OPEN WITHDRAWALS', to: '/admin/withdrawals',
      note: c.pendingWithdrawals ? 'Stuck or failed payouts that need a retry or a decision.' : 'Nothing waiting.',
    },
    can('listed_pros', 'view') && {
      label: 'PRO CLAIMS', count: c.listedProClaims, cta: 'OPEN CLAIMS', to: '/admin/listed-pros',
      note: c.listedProClaims ? 'Businesses claiming their listed page, and removal requests behind them.' : 'Nothing waiting.',
    },
    can('newsletter', 'view') && {
      label: 'UNSENT DRAFT', count: c.newsletterDrafts, cta: 'OPEN DRAFT', to: '/admin/newsletter',
      note: c.newsletterDrafts ? `${c.newsletterDrafts} ${plural(c.newsletterDrafts, 'draft is', 'drafts are')} unsent. Not scheduled.` : 'Nothing is unsent.',
    },
    can('party_engine', 'view') && (c.engineAwaitingReview || 0) > 0 && {
      label: 'CONCEPTS TO REVIEW', count: c.engineAwaitingReview, cta: 'OPEN PARTY ENGINE', to: '/admin/party-engine',
      note: 'Party Engine runs parked in review with concepts waiting on a human.',
    },
    isSuperadmin && {
      label: 'INVITE PENDING', count: c.pendingInvites, cta: 'OPEN STAFF', to: '/admin/staff',
      note: c.pendingInvites ? 'A key that has not been picked up yet. Links last seven days.' : 'Nobody is waiting on a key.',
    },
  ].filter(Boolean);

  return (
    <>
      <AdminPageHeader section="today" meta="Only the queues you can act on. Nothing here is a number for its own sake." />
      <div className="flex flex-col gap-5">
        {cards.length > 0 && (
          <div className="grid gap-[14px]" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
            {cards.map((card) => {
              const hot = (card.count || 0) > 0;
              return (
                <article key={card.label} className="relative flex min-h-[200px] flex-col gap-[14px] overflow-hidden rounded-[24px] bg-white p-[22px]">
                  {hot && <span aria-hidden="true" className="absolute left-0 right-0 top-0 h-[3px]" style={{ background: RING }} />}
                  <div className="flex items-start justify-between gap-3">
                    <span className="font-mono text-[11px] font-bold uppercase leading-[1.5] tracking-[0.16em]">{card.label}</span>
                    <span
                      className="flex h-11 min-w-[44px] items-center justify-center rounded-full px-3 font-mono text-[18px] font-extrabold"
                      style={hot ? { background: '#000', color: '#fff' } : { background: '#F2F2F2', color: '#9CA3AF' }}
                    >
                      {n(card.count)}
                    </span>
                  </div>
                  <span className="font-mono text-[12px] leading-[1.6] text-[#6B7280]">{card.note}</span>
                  <button
                    type="button"
                    onClick={() => navigate(card.to)}
                    className={`mt-auto h-10 self-start rounded-full px-[18px] font-mono text-[10px] font-bold uppercase tracking-[0.14em] transition-colors duration-200 ${hot ? 'bg-black text-white hover:opacity-85' : 'bg-[#F2F2F2] text-black hover:bg-black hover:text-white'}`}
                  >
                    {card.cta}
                  </button>
                </article>
              );
            })}
          </div>
        )}
        <NoteCard>Only what is waiting on a human, filtered to what you can act on. Totals live in STATS.</NoteCard>
      </div>
    </>
  );
}
